import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { Calendar } from '@vk-hr-tek/core/calendar';
import { CommonFilter, Filter } from '@vk-hr-tek/core/filter';

import { EditorNewsListItem, EditorNewsListResponse } from '@app/gen/news';

import { DraggableNewsItemType } from '../../types';

const FILTERS_KEYS_FOR_WIDGET_CONTENT = [
  'title_search',
  'author_ids',
  'is_important',
];

const WIDGET_SIZE_SMALL = 1;
const WIDGET_SIZE_MEDIUM = 2;
const WIDGET_SIZE_LARGE = 3;

const MAX_FILTER_WIDTH: CommonFilter['width'] = 12;

@injectable()
export class WidgetContentNewsListMapper {
  constructor(
    private format: FormatService,
    private calendar: Calendar,
  ) {}

  private formatAuthorName(
    firstName: string,
    middleName: string,
    secondName: string,
  ): string {
    return `${secondName} ${firstName[0]}.${
      middleName ? ` ${middleName[0]}.` : ''
    }`;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (!this.calendar.isValidDate(date)) return dateString;

    const isoDate = this.calendar.formatISO(
      this.calendar.subMinutes(new Date(dateString), 0),
    );

    return `${this.format.toDate(isoDate)} ${this.format.toTime(isoDate)}`;
  }

  public processListEntity(newsItem: EditorNewsListItem) {
    return {
      id: newsItem.id,
      coverId: newsItem.cover_att_id,
      title: newsItem.title,
      isImportant: newsItem.is_important,
      publishedAt: this.formatDate(newsItem.published_at),
      author: this.formatAuthorName(
        newsItem.author.first_name,
        newsItem.author.middle_name,
        newsItem.author.second_name,
      ),
    };
  }

  public processListResponse(
    news: EditorNewsListResponse['news_list'],
    newsIds: string[],
    extraNews: DraggableNewsItemType[],
  ) {
    return [
      ...news
        .map((item) => this.processListEntity(item))
        .filter(
          (item) =>
            !newsIds.includes(item.id) &&
            !extraNews.some((e) => e.id === item.id),
        ),
      ...extraNews.filter((item) => !newsIds.includes(item.id)),
    ];
  }

  public processFilters(filters: Filter[] | null): Filter[] {
    if (!filters) return [];

    const availableFilters = filters
      .filter(({ key }) => FILTERS_KEYS_FOR_WIDGET_CONTENT.includes(key))
      .map((filter) => {
        switch (filter.key) {
          case 'title_search':
            return {
              ...filter,
              fullWidth: true,
              width: MAX_FILTER_WIDTH,
            };
          default:
            return filter;
        }
      });

    return availableFilters;
  }

  public processWidgetSize(widgetSize?: number) {
    if (!widgetSize) return undefined;
    switch (widgetSize) {
      case WIDGET_SIZE_SMALL:
        return 's';
      case WIDGET_SIZE_MEDIUM:
        return 'm';
      case WIDGET_SIZE_LARGE:
        return 'l';
    }
  }
}
